import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
    FiX, FiUser, FiMail, FiShield, FiBriefcase,
    FiClock, FiCalendar, FiActivity, FiUsers, FiAward,
    FiLock, FiEye, FiEyeOff, FiCamera, FiTrash2,
    FiChevronDown, FiSend, FiXCircle
} from 'react-icons/fi';
import '../styles/MyProfile.css';

// Determine and sanitize API base used to build absolute URLs.
// Vite envs are baked at build time; a malformed VITE_API_URL will cause double-host links.
const rawApiEnv = import.meta.env.VITE_API_URL;
const sanitizeApiBase = (raw) => {
    if (!raw) {
        if (typeof window !== 'undefined' && window.location) return window.location.origin;
        return 'http://localhost:8080';
    }
    let v = String(raw).trim();
    // Normalize protocol slashes: 'https:/' -> 'https://'
    v = v.replace(/^(https?:)\/(?!\/)/i, '$1//');
    // Remove any leading garbage before the last occurrence of http(s)
    const lastHttp = v.lastIndexOf('http');
    if (lastHttp > 0) v = v.slice(lastHttp);
    // Remove trailing slashes
    v = v.replace(/\/+$/, '');
    // Remove '/api' suffix if present (we want origin only)
    v = v.replace(/\/api$/i, '');
    return v;
};

const API_BASE = sanitizeApiBase(rawApiEnv);

const MyProfile = ({ isOpen, onClose }) => {
    const { user, updateUserStatus } = useAuth();
    const fileInputRef = useRef(null);
    const [profile, setProfile] = useState(null);
    const [attendance, setAttendance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Active tab
    const [activeTab, setActiveTab] = useState('profile');

    // Profile picture
    const [uploadingPic, setUploadingPic] = useState(false);

    // Status
    const [currentStatus, setCurrentStatus] = useState('');
    const [statusLoading, setStatusLoading] = useState(false);

    // Change password state
    const [showPasswordSection, setShowPasswordSection] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPw, setShowCurrentPw] = useState(false);
    const [showNewPw, setShowNewPw] = useState(false);
    const [showConfirmPw, setShowConfirmPw] = useState(false);
    const [pwLoading, setPwLoading] = useState(false);
    const [pwMessage, setPwMessage] = useState({ type: '', text: '' });

    // Leave requests
    const [leaves, setLeaves] = useState([]);
    const [leavesLoading, setLeavesLoading] = useState(false);
    const [leaveForm, setLeaveForm] = useState({
        leaveType: 'casual',
        startDate: '',
        endDate: '',
        reason: ''
    });
    const [leaveSubmitting, setLeaveSubmitting] = useState(false);
    const [leaveMessage, setLeaveMessage] = useState({ type: '', text: '' });

    const statuses = ['Working', 'On Break', 'Busy', 'Not Available'];

    useEffect(() => {
        if (isOpen) {
            fetchProfile();
            resetPasswordForm();
            setActiveTab('profile');
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && activeTab === 'leaves') {
            fetchLeaves();
        }
    }, [isOpen, activeTab]);

    const resetPasswordForm = () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowCurrentPw(false);
        setShowNewPw(false);
        setShowConfirmPw(false);
        setPwMessage({ type: '', text: '' });
        setShowPasswordSection(false);
    };

    // =================== FETCH PROFILE ===================
    const fetchProfile = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await api.get('/users/profile');
            setProfile(res.data.profile);
            setAttendance(res.data.attendance);
            setCurrentStatus(res.data.profile?.status || 'Not Available');
        } catch (err) {
            console.error('Error fetching profile:', err);
            setError('Failed to load profile data');
        } finally {
            setLoading(false);
        }
    };

    // =================== PROFILE PICTURE ===================
    const handlePictureUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('avatar', file);

        try {
            setUploadingPic(true);
            const res = await api.post('/users/profile-picture', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setProfile(prev => ({ ...prev, profilePicture: res.data.profilePicture }));
        } catch (err) {
            console.error('Upload error:', err);
            alert(err.response?.data?.message || 'Failed to upload picture');
        } finally {
            setUploadingPic(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handlePictureRemove = async () => {
        try {
            setUploadingPic(true);
            await api.delete('/users/profile-picture');
            setProfile(prev => ({ ...prev, profilePicture: '' }));
        } catch (err) {
            console.error('Remove error:', err);
        } finally {
            setUploadingPic(false);
        }
    };

    // =================== STATUS CHANGE ===================
    const handleStatusChange = async (status) => {
        try {
            setStatusLoading(true);
            await api.put('/users/status', { status });
            setCurrentStatus(status);
            setProfile(prev => ({ ...prev, status }));
            if (updateUserStatus) updateUserStatus(status);
        } catch (err) {
            console.error('Status update error:', err);
        } finally {
            setStatusLoading(false);
        }
    };

    // =================== CHANGE PASSWORD ===================
    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPwMessage({ type: '', text: '' });

        if (!currentPassword || !newPassword || !confirmPassword) {
            setPwMessage({ type: 'error', text: 'All fields are required' });
            return;
        }
        if (newPassword.length < 6) {
            setPwMessage({ type: 'error', text: 'New password must be at least 6 characters' });
            return;
        }
        if (newPassword !== confirmPassword) {
            setPwMessage({ type: 'error', text: 'New passwords do not match' });
            return;
        }

        try {
            setPwLoading(true);
            const res = await api.put('/users/change-password', { currentPassword, newPassword });
            setPwMessage({ type: 'success', text: res.data.message || 'Password changed successfully!' });
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            setPwMessage({ type: 'error', text: err.response?.data?.message || 'Failed to change password' });
        } finally {
            setPwLoading(false);
        }
    };

    // =================== LEAVE REQUESTS ===================
    const fetchLeaves = async () => {
        try {
            setLeavesLoading(true);
            const res = await api.get('/users/leaves');
            setLeaves(res.data.leaves || []);
        } catch (err) {
            console.error('Error fetching leaves:', err);
        } finally {
            setLeavesLoading(false);
        }
    };

    const handleLeaveSubmit = async (e) => {
        e.preventDefault();
        setLeaveMessage({ type: '', text: '' });

        if (!leaveForm.leaveType || !leaveForm.startDate || !leaveForm.endDate || !leaveForm.reason) {
            setLeaveMessage({ type: 'error', text: 'All fields are required' });
            return;
        }

        try {
            setLeaveSubmitting(true);
            const res = await api.post('/users/leaves', leaveForm);
            setLeaveMessage({ type: 'success', text: res.data.message || 'Leave request submitted!' });
            setLeaveForm({ leaveType: 'casual', startDate: '', endDate: '', reason: '' });
            fetchLeaves();
        } catch (err) {
            setLeaveMessage({ type: 'error', text: err.response?.data?.message || 'Failed to submit leave request' });
        } finally {
            setLeaveSubmitting(false);
        }
    };

    const handleCancelLeave = async (id) => {
        if (!confirm('Cancel this leave request?')) return;
        try {
            await api.delete(`/users/leaves/${id}`);
            fetchLeaves();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to cancel leave');
        }
    };

    // =================== HELPERS ===================
    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-IN', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleTimeString('en-IN', {
            hour: '2-digit', minute: '2-digit'
        });
    };

    const formatHours = (hours) => {
        if (!hours && hours !== 0) return '—';
        const h = Math.floor(hours);
        const m = Math.round((hours - h) * 60);
        return `${h}h ${m}m`;
    };

    const getRoleBadgeClass = (role) => {
        switch (role) {
            case 'admin': return 'role-admin';
            case 'hr': return 'role-hr';
            case 'manager': return 'role-manager';
            case 'employee': return 'role-employee';
            default: return 'role-employee';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Working': return '#10b981';
            case 'On Break': return '#f59e0b';
            case 'Busy': return '#ef4444';
            case 'Not Available': return '#6b7280';
            default: return '#6b7280';
        }
    };

    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    };

    const getLeaveStatusClass = (status) => {
        switch (status) {
            case 'approved': return 'leave-approved';
            case 'rejected': return 'leave-rejected';
            default: return 'leave-pending';
        }
    };

    const getLeaveTypeLabel = (type) => {
        const labels = {
            sick: 'Sick Leave',
            casual: 'Casual Leave',
            earned: 'Earned Leave',
            unpaid: 'Unpaid Leave',
            other: 'Other',
            half_day: 'Half Day',
            early_leave: 'Early Leave'
        };
        return labels[type] || type;
    };

    if (!isOpen) return null;

    // Normalize and build avatar URL to avoid malformed stored values like
    // 'https:/.domain.../api/uploads/...' which cause double-host concatenation.
    const buildAvatarUrl = (picPath) => {
        if (!picPath) return null;

        // If it's already a proper absolute URL, use it
        if (/^https?:\/\//i.test(picPath)) return picPath;

        // If the stored value accidentally contains the uploads segment, extract from there
        const uploadsIndex = picPath.indexOf('/uploads');
        if (uploadsIndex !== -1) {
          picPath = picPath.slice(uploadsIndex);
        } else {
            // Fix common malformed protocol cases like 'https:/.example.com/...'
            picPath = picPath.replace(/^(https?:)\/(?!\/)/i, '$1//');
        }

        // Now ensure we return a full URL by joining with API_BASE (no trailing slash)
        const base = (API_BASE || '').replace(/\/+$/, '');
        const path = picPath.startsWith('/') ? picPath : `/${picPath}`;
        return `${base}${path}`;
    };

    const avatarUrl = buildAvatarUrl(profile?.profilePicture);

    return (
        <div className="profile-overlay" onClick={onClose}>
            <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
                <button className="profile-close-btn" onClick={onClose}>
                    <FiX size={20} />
                </button>

                {loading ? (
                    <div className="profile-loading">
                        <div className="profile-spinner" />
                        <p>Loading profile...</p>
                    </div>
                ) : error ? (
                    <div className="profile-error">
                        <p>{error}</p>
                        <button onClick={fetchProfile}>Retry</button>
                    </div>
                ) : (
                    <>
                        {/* Header Section */}
                        <div className="profile-header">
                            <div className="profile-header-bg" />
                            <div className="profile-avatar-section">
                                <div className="profile-avatar-wrapper">
                                    <div className="profile-avatar">
                                        {avatarUrl ? (
                                            <img src={avatarUrl} alt="Profile" className="avatar-image" width={88} height={88} />
                                        ) : (
                                            <span>{getInitials(profile?.username)}</span>
                                        )}
                                        <span
                                            className="profile-status-dot"
                                            style={{ backgroundColor: getStatusColor(currentStatus) }}
                                            title={currentStatus}
                                        />
                                    </div>
                                    {/* Camera overlay */}
                                    <button
                                        className="avatar-edit-btn"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploadingPic}
                                        title="Change profile picture"
                                    >
                                        {uploadingPic ? <div className="avatar-edit-spinner" /> : <FiCamera size={14} />}
                                    </button>
                                    {avatarUrl && (
                                        <button
                                            className="avatar-remove-btn"
                                            onClick={handlePictureRemove}
                                            disabled={uploadingPic}
                                            title="Remove profile picture"
                                        >
                                            <FiTrash2 size={12} />
                                        </button>
                                    )}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/gif,image/webp"
                                        style={{ display: 'none' }}
                                        onChange={handlePictureUpload}
                                    />
                                </div>
                                <div className="profile-name-section">
                                    <h1>{profile?.username}</h1>
                                    <span className={`profile-role-badge ${getRoleBadgeClass(profile?.role)}`}>
                                        <FiShield size={13} />
                                        {profile?.role?.charAt(0).toUpperCase() + profile?.role?.slice(1)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Tab Navigation */}
                        <div className="profile-tabs">
                            <button
                                className={`profile-tab ${activeTab === 'profile' ? 'active' : ''}`}
                                onClick={() => setActiveTab('profile')}
                            >
                                <FiUser size={15} /> Profile
                            </button>
                            <button
                                className={`profile-tab ${activeTab === 'leaves' ? 'active' : ''}`}
                                onClick={() => setActiveTab('leaves')}
                            >
                                <FiCalendar size={15} /> Request Leave
                            </button>
                        </div>

                        {/* Tab Content */}
                        <div className="profile-body">
                            {activeTab === 'profile' && (
                                <>
                                    {/* Status Changer */}
                                    <div className="profile-section">
                                        <h2 className="profile-section-title">
                                            <FiActivity size={18} />
                                            Current Status
                                        </h2>
                                        <div className="status-changer-grid">
                                            {statuses.map(st => (
                                                <button
                                                    key={st}
                                                    className={`status-chip ${currentStatus === st ? 'status-chip-active' : ''}`}
                                                    onClick={() => handleStatusChange(st)}
                                                    disabled={statusLoading}
                                                    style={{
                                                        '--chip-color': getStatusColor(st)
                                                    }}
                                                >
                                                    <span className="status-chip-dot" style={{ backgroundColor: getStatusColor(st) }} />
                                                    {st}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Info Cards */}
                                    <div className="profile-info-grid">
                                        <div className="profile-info-card">
                                            <div className="info-card-icon"><FiMail size={18} /></div>
                                            <div className="info-card-content">
                                                <span className="info-label">Email</span>
                                                <span className="info-value">{profile?.email}</span>
                                            </div>
                                        </div>
                                        <div className="profile-info-card">
                                            <div className="info-card-icon"><FiBriefcase size={18} /></div>
                                            <div className="info-card-content">
                                                <span className="info-label">Department</span>
                                                <span className="info-value">{profile?.department || 'Unassigned'}</span>
                                            </div>
                                        </div>
                                        <div className="profile-info-card">
                                            <div className="info-card-icon"><FiUsers size={18} /></div>
                                            <div className="info-card-content">
                                                <span className="info-label">Reports To</span>
                                                <span className="info-value">{profile?.managerName || 'None'}</span>
                                            </div>
                                        </div>
                                        <div className="profile-info-card">
                                            <div className="info-card-icon"><FiCalendar size={18} /></div>
                                            <div className="info-card-content">
                                                <span className="info-label">Member Since</span>
                                                <span className="info-value">{formatDate(profile?.createdAt)}</span>
                                            </div>
                                        </div>
                                        <div className="profile-info-card">
                                            <div className="info-card-icon"><FiAward size={18} /></div>
                                            <div className="info-card-content">
                                                <span className="info-label">Account Status</span>
                                                <span className={`info-value ${profile?.isActive ? 'text-green' : 'text-red'}`}>
                                                    {profile?.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Attendance Stats */}
                                    <div className="profile-section">
                                        <h2 className="profile-section-title">
                                            <FiClock size={18} />
                                            Attendance Overview
                                        </h2>
                                        <div className="attendance-stats-grid">
                                            <div className="attendance-stat-card stat-hours">
                                                <div className="stat-number">{formatHours(attendance?.monthStats?.totalHours)}</div>
                                                <div className="stat-label">Hours This Month</div>
                                            </div>
                                            <div className="attendance-stat-card stat-days">
                                                <div className="stat-number">{attendance?.monthStats?.daysWorked || 0}</div>
                                                <div className="stat-label">Days Worked</div>
                                            </div>
                                            <div className="attendance-stat-card stat-sessions">
                                                <div className="stat-number">{attendance?.monthStats?.totalSessions || 0}</div>
                                                <div className="stat-label">Sessions This Month</div>
                                            </div>
                                            <div className="attendance-stat-card stat-total">
                                                <div className="stat-number">{attendance?.allTimeSessionCount || 0}</div>
                                                <div className="stat-label">Total Sessions</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Today's Sessions */}
                                    {attendance?.todaySessions?.length > 0 && (
                                        <div className="profile-section">
                                            <h2 className="profile-section-title">
                                                <FiCalendar size={18} />
                                                Today's Sessions
                                            </h2>
                                            <div className="today-sessions-list">
                                                {attendance.todaySessions.map((session, idx) => (
                                                    <div key={idx} className="session-row">
                                                        <div className="session-time">
                                                            <span className="session-clock-in">{formatTime(session.loginTime)}</span>
                                                            <span className="session-separator">→</span>
                                                            <span className="session-clock-out">
                                                                {session.logoutTime ? formatTime(session.logoutTime) : (
                                                                    <span className="session-active-badge">Active Now</span>
                                                                )}
                                                            </span>
                                                        </div>
                                                        <div className={`session-status-indicator ${session.isActive ? 'active' : 'ended'}`} />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Change Password */}
                                    <div className="profile-section">
                                        <h2
                                            className="profile-section-title profile-section-toggle"
                                            onClick={() => setShowPasswordSection(!showPasswordSection)}
                                        >
                                            <FiLock size={18} />
                                            Change Password
                                            <span className={`toggle-chevron ${showPasswordSection ? 'open' : ''}`}>▾</span>
                                        </h2>

                                        {showPasswordSection && (
                                            <form className="password-form" onSubmit={handleChangePassword}>
                                                {pwMessage.text && (
                                                    <div className={`pw-message pw-message-${pwMessage.type}`}>
                                                        {pwMessage.text}
                                                    </div>
                                                )}
                                                <div className="pw-field">
                                                    <label>Current Password</label>
                                                    <div className="pw-input-wrapper">
                                                        <input
                                                            type={showCurrentPw ? 'text' : 'password'}
                                                            value={currentPassword}
                                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                                            placeholder="Enter current password"
                                                            autoComplete="current-password"
                                                        />
                                                        <button type="button" className="pw-toggle-btn" onClick={() => setShowCurrentPw(!showCurrentPw)} tabIndex={-1}>
                                                            {showCurrentPw ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="pw-field">
                                                    <label>New Password</label>
                                                    <div className="pw-input-wrapper">
                                                        <input
                                                            type={showNewPw ? 'text' : 'password'}
                                                            value={newPassword}
                                                            onChange={(e) => setNewPassword(e.target.value)}
                                                            placeholder="Enter new password (min 6 chars)"
                                                            autoComplete="new-password"
                                                        />
                                                        <button type="button" className="pw-toggle-btn" onClick={() => setShowNewPw(!showNewPw)} tabIndex={-1}>
                                                            {showNewPw ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="pw-field">
                                                    <label>Confirm New Password</label>
                                                    <div className="pw-input-wrapper">
                                                        <input
                                                            type={showConfirmPw ? 'text' : 'password'}
                                                            value={confirmPassword}
                                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                                            placeholder="Confirm new password"
                                                            autoComplete="new-password"
                                                        />
                                                        <button type="button" className="pw-toggle-btn" onClick={() => setShowConfirmPw(!showConfirmPw)} tabIndex={-1}>
                                                            {showConfirmPw ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                                                        </button>
                                                    </div>
                                                </div>
                                                <button type="submit" className="pw-submit-btn" disabled={pwLoading}>
                                                    {pwLoading ? (
                                                        <><span className="pw-btn-spinner" /> Updating...</>
                                                    ) : (
                                                        <><FiLock size={15} /> Update Password</>
                                                    )}
                                                </button>
                                            </form>
                                        )}
                                    </div>
                                </>
                            )}

                            {activeTab === 'leaves' && (
                                <>
                                    {/* Leave Request Form */}
                                    <div className="profile-section">
                                        <h2 className="profile-section-title">
                                            <FiSend size={18} />
                                            Submit Leave Request
                                        </h2>

                                        <form className="leave-form" onSubmit={handleLeaveSubmit}>
                                            {leaveMessage.text && (
                                                <div className={`pw-message pw-message-${leaveMessage.type}`}>
                                                    {leaveMessage.text}
                                                </div>
                                            )}

                                            <div className="leave-form-row">
                                                <div className="pw-field">
                                                    <label>Leave Type</label>
                                                    <select
                                                        className="leave-select"
                                                        value={leaveForm.leaveType}
                                                        onChange={(e) => setLeaveForm(f => ({ ...f, leaveType: e.target.value }))}
                                                    >
                                                        <option value="casual">Casual Leave</option>
                                                        <option value="sick">Sick Leave</option>
                                                        <option value="half_day">Half Day</option>
                                                        <option value="early_leave">Early Leave</option>
                                                        <option value="earned">Earned Leave</option>
                                                        <option value="unpaid">Unpaid Leave</option>
                                                        <option value="other">Other</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="leave-form-row leave-form-dates">
                                                <div className="pw-field">
                                                    <label>Start Date</label>
                                                    <input
                                                        type="date"
                                                        className="leave-date-input"
                                                        value={leaveForm.startDate}
                                                        onChange={(e) => setLeaveForm(f => ({ ...f, startDate: e.target.value }))}
                                                    />
                                                </div>
                                                <div className="pw-field">
                                                    <label>End Date</label>
                                                    <input
                                                        type="date"
                                                        className="leave-date-input"
                                                        value={leaveForm.endDate}
                                                        onChange={(e) => setLeaveForm(f => ({ ...f, endDate: e.target.value }))}
                                                    />
                                                </div>
                                            </div>

                                            <div className="pw-field">
                                                <label>Reason</label>
                                                <textarea
                                                    className="leave-textarea"
                                                    value={leaveForm.reason}
                                                    onChange={(e) => setLeaveForm(f => ({ ...f, reason: e.target.value }))}
                                                    placeholder="Describe the reason for your leave..."
                                                    rows={3}
                                                />
                                            </div>

                                            <button type="submit" className="pw-submit-btn" disabled={leaveSubmitting}>
                                                {leaveSubmitting ? (
                                                    <><span className="pw-btn-spinner" /> Submitting...</>
                                                ) : (
                                                    <><FiSend size={15} /> Submit Request</>
                                                )}
                                            </button>
                                        </form>
                                    </div>


                                    {/* Leave History */}
                                    <div className="profile-section">
                                        <h2 className="profile-section-title">
                                            <FiCalendar size={18} />
                                            My Leave Requests
                                        </h2>

                                        {leavesLoading ? (
                                            <div className="profile-loading" style={{ padding: '2rem' }}>
                                                <div className="profile-spinner" />
                                            </div>
                                        ) : leaves.length === 0 ? (
                                            <div className="leave-empty">
                                                <FiCalendar size={32} />
                                                <p>No leave requests yet</p>
                                            </div>
                                        ) : (
                                            <div className="leave-list">
                                                {leaves.map(leave => (
                                                    <div key={leave._id} className="leave-card">
                                                        <div className="leave-card-header">
                                                            <span className="leave-type-badge">{getLeaveTypeLabel(leave.leaveType)}</span>
                                                            <span className={`leave-status-badge ${getLeaveStatusClass(leave.status)}`}>
                                                                {leave.status.charAt(0).toUpperCase() + leave.status.slice(1)}
                                                            </span>
                                                        </div>
                                                        <div className="leave-card-dates">
                                                            <span>{formatDate(leave.startDate)}</span>
                                                            <span className="session-separator">→</span>
                                                            <span>{formatDate(leave.endDate)}</span>
                                                        </div>
                                                        <p className="leave-card-reason">{leave.reason}</p>

                                                        {/* Approval Checkpoints Timeline */}
                                                        {leave.approvals && leave.approvals.length > 0 && (
                                                            <div className="approval-timeline">
                                                                <div className="approval-timeline-title">Approval Progress</div>
                                                                {leave.approvals.map((appr, idx) => (
                                                                    <div key={appr._id || idx} className={`approval-step approval-step-${appr.status}`}>
                                                                        <div className="approval-step-icon">
                                                                            {appr.status === 'approved' ? '✓' : appr.status === 'rejected' ? '✗' : '○'}
                                                                        </div>
                                                                        <div className="approval-step-content">
                                                                            <div className="approval-step-header">
                                                                                <span className="approval-step-name">{appr.approverName}</span>
                                                                                <span className="approval-step-label">{appr.label}</span>
                                                                            </div>
                                                                            <div className="approval-step-status">
                                                                                <span className={`approval-badge approval-badge-${appr.status}`}>
                                                                                    {appr.status.charAt(0).toUpperCase() + appr.status.slice(1)}
                                                                                </span>
                                                                                {appr.respondedAt && (
                                                                                    <span className="approval-step-date">{formatDate(appr.respondedAt)}</span>
                                                                                )}
                                                                            </div>
                                                                            {appr.comment && (
                                                                                <div className="approval-step-comment">
                                                                                    "{appr.comment}"
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {leave.status === 'pending' && (
                                                            <button
                                                                className="leave-cancel-btn"
                                                                onClick={() => handleCancelLeave(leave._id)}
                                                            >
                                                                <FiXCircle size={14} /> Cancel
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default MyProfile;
